import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import TableContainer from "Components/Common/TableContainer";
import { getPersons, deletePerson } from "common/services/persons.service";
import { getSortObj } from "common/lib/tableDataOptions";
import { createTableSearchParams, parseTableFilters, parseTableSearch, parseTableSort } from "common/lib/tableQueryParams";
import * as moment from "moment";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import BulkActionBar from "Components/Common/BulkActionBar";
import DeleteModal from "Components/Common/DeleteModal";
import { normalize } from "common/lib/normalize";
import { clearTableCheckboxes } from "common/lib/tableSelection";
import { toast } from "react-toastify";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import Import from "./Import";
import {
  FilterSearchBox,
  FilterValueSelect,
} from "Components/Common/TableFilters";

interface FilterValue {
  gender: string;
}

const AllPersons = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [searchValue, setSearchValue] = useState(parseTableSearch(searchParams));
  const defaultFilterValue: FilterValue = {
    gender: "",
  };
  const [filterValue, setFilterValue] = useState(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );

  const unCheckAll = useCallback(() => {
    clearTableCheckboxes();
    setSelectedCheckBox([]);
  }, []);

  const [apiParams, setApiParams] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions = [];
      if (filterValue?.gender) {
        andConditions.push({
          gender: {
            $eq: filterValue.gender,
          },
        });
      }

      andConditions.push({
        $or: [
          {
            fullName: {
              $containsi: searchValue,
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();

        const apiParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "alias",
            "contactDetails",
            "flaggedBy",
            "createdby",
            "updatedby",
            "currentNationality",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(apiParams);
        const response: any = await getPersons(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, setSearchParams, unCheckAll]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue, filterValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deletePerson({
          id: selectedRecord.id,
        });
        fetchUpdatedData();
        setDeleting(false);
        setDeleteModal(false);
        toast.success("Person deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting person");
      } finally {
        setDeleting(false);
      }
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });
    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deletePerson({ id });
          })
        );
        setDeleting(false);
        toast.success("Persons deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "person",
        action: "delete",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { cellClass: "table-checkbox-column" },
      },
      {
        header: "Name",
        accessorKey: "firstName",
        id: "firstName",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 avatar-xs me-3">
                  <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-16">
                    <i className={"ri-user-line"}></i>
                  </div>
                </div>
                <Link
                  to={`/persons/${cell.row.original?.id}`}
                  className="fw-medium link-primary"
                >
                  {`${rowData?.fullName || ""}`}
                </Link>
              </div>
            </>
          );
        },
      },
      {
        header: "Gender",
        accessorKey: "gender",
        enableColumnFilter: false,
        id: "gender",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Nationality",
        accessorKey: "currentNationality.name",
        id: "currentNationality.name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Date Added",
        accessorKey: "createdAt",
        id: "createdAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },
      {
        header: "Action",
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="person" action="findOne">
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/persons/${rowData.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission resource="person" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col xxl={8} lg={6}>
              <FilterSearchBox
                id="person-search"
                placeholder="Search people..."
                value={searchValue}
                onChange={(value: string) => {
                  setSearchValue(value);
                  setPage(1);
                }}
              />
            </Col>
            <Col xxl={4} lg={6}>
              <FilterValueSelect
                id="genderFilter"
                label="Gender"
                placeholder="Select Gender"
                value={filterValue.gender}
                onChange={(value: any) => {
                  setFilterValue({ ...filterValue, gender: value });
                  setPage(1);
                }}
                options={[
                  { value: "Male", label: "Male" },
                  { value: "Female", label: "Female" },
                ]}
              />
            </Col>

          </Row>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <Row className="align-items-center g-3">
            <Col md={9}>
              <h5 className="card-title mb-1">Persons</h5>
              <p className="mb-0 text-muted">
                This list constitutes of all individuals inclusive of the ones
                identified in Panel of Experts reports (POE) and Interpol List.
              </p>
            </Col>
            <div className="col-md-auto ms-auto">
              <div className="d-flex gap-2">
                <Permission resource="person" action="create">
                  <Link to="/persons/add" className="btn btn-primary">
                    <i className="ri-add-line align-bottom me-1"></i>
                    Add Person
                  </Link>
                </Permission>

                <Permissions
                  checks={[
                    {
                      resource: "person",
                      action: "import",
                    },
                    {
                      resource: "person",
                      action: "export",
                    },
                  ]}
                >
                  <Import apiParams={apiParams} onImported={fetchUpdatedData} />
                </Permissions>
              </div>
            </div>
          </Row>
        </CardHeader>
        <CardBody>
          <TableContainer
            loading={loading}
            columns={columns}
            data={data || []}
            pagination={pagination}
            setPagination={setPagination}
            sorted={sorted}
            setSorted={setSorted}
            onPageChange={onPageChange}
            FilterSection={() => {}}
            totalRows={meta?.pagination?.total || 0}
            totalPages={meta?.pagination?.pageCount || 0}
            searchValue={searchValue}
            filterValue={filterValue}
            columnVisibility={columnVisibility}
            divClass="table-responsive table-card mb-4"
            tableClass="align-middle table-nowrap mb-0 table-striped"
            theadClass="table-light table-nowrap"
          />
          <DeleteModal
            show={deleteModal}
            onDeleteClick={() => handleDelete()}
            onCloseClick={() => setDeleteModal(false)}
            loading={deleting}
          />
          <DeleteModal
            show={deleteMultipleModal}
            onDeleteClick={() => handleDeleteMultiple()}
            onCloseClick={() => setDeleteMultipleModal(false)}
            loading={deleting}
          />
          <Permissions checks={[{ resource: "person", action: "delete" }]}>
            <BulkActionBar
              count={selectedCheckBox.length}
              onClear={unCheckAll}
              itemLabel="person"
            >
              <Button
                color="danger"
                size="sm"
                className="material-shadow-none"
                onClick={() => setDeleteMultipleModal(true)}
              >
                <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
              </Button>
            </BulkActionBar>
          </Permissions>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default AllPersons;
